// src/components/AspirantometroCard.js
import React, { useState, useEffect } from 'react';

// Função para calcular dias restantes e porcentagem com precisão no fuso local
const calculateTimeRemaining = (targetDateStr) => {
  // 1. Cria a data atual e zera o horário rigorosamente no fuso do Brasil
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  // 2. Desmembra a string "YYYY-MM-DD" para evitar qualquer interferência do fuso UTC
  const [year, month, day] = targetDateStr.split('-');
  
  // 3. Cria a data alvo localmente (lembrete: meses no JS começam em 0) e zera as horas
  const targetDate = new Date(year, month - 1, day);
  targetDate.setHours(0, 0, 0, 0);
  
  // Calcula a diferença em milissegundos
  const diffTime = targetDate.getTime() - now.getTime();
  
  // Usamos Math.round para evitar qualquer anomalia de fração e cravar os dias absolutos
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays <= 0) {
    return {
      diasRestantes: 0,
      porcentagem: 100,
      status: 'Concluído'
    };
  }

  // Base do CFO: 4 anos
  const TOTAL_DIAS_CFO = 1460; 

  const diasPassados = TOTAL_DIAS_CFO - diffDays;
  // Math.max(0, ...) garante que a porcentagem não fique negativa se a data for muito no futuro
  const porcentagem = Math.min(100, Math.max(0, Math.round((diasPassados / TOTAL_DIAS_CFO) * 100)));
  
  return {
    diasRestantes: diffDays,
    porcentagem: porcentagem,
    status: 'Em Andamento'
  };
};

export const AspirantometroCard = ({ aspirantometro }) => {
    const { titulo, data_final } = aspirantometro;
    const [timeData, setTimeData] = useState(calculateTimeRemaining(data_final));

    // Atualiza a cada 1 minuto (60000 ms)
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeData(calculateTimeRemaining(data_final));
        }, 60000); 

        return () => clearInterval(timer);
    }, [data_final]);

    const { diasRestantes, porcentagem, status } = timeData;
    
    // Cores: se concluído, usa cinza. Senão, usa o azul padrão.
    const circleColor = status === 'Concluído' ? 'text-gray-300' : 'text-blue-500';
    
    // Calcula o offset do círculo (CSS Trick para Doughnut Chart)
    const radius = 40; // Raio do círculo
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (porcentagem / 100) * circumference;

    // Formatação segura da data de conclusão para exibir no rodapé sem errar o dia
    const formatarDataConclusao = (dataStr) => {
        const [y, m, d] = dataStr.split('-');
        return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
    };

    return (
        <div className="w-full bg-white rounded-xl shadow-lg p-6 flex flex-col items-center justify-center transition-shadow hover:shadow-xl">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 text-center truncate">{titulo}</h3>
            
            <div className="relative h-24 w-24 mb-4">
                <svg className="w-full h-full transform -rotate-90">
                    {/* Fundo do círculo (Cinza claro) */}
                    <circle 
                        className="text-gray-200"
                        strokeWidth="8"
                        stroke="currentColor"
                        fill="transparent"
                        r={radius}
                        cx="50%"
                        cy="50%"
                    />
                    {/* Círculo de progresso (Cor dinâmica) */}
                    <circle 
                        className={circleColor}
                        strokeWidth="8"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r={radius}
                        cx="50%"
                        cy="50%"
                    />
                </svg>
                <span className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-xl font-bold text-gray-800">
                    {porcentagem}%
                </span>
            </div>

            <p className="text-5xl font-extrabold mb-1" style={{ color: '#3b82f6' }}>
                {diasRestantes}
            </p>
            <p className="text-base text-gray-500 uppercase tracking-wider">
                {diasRestantes === 1 ? 'Dia Restante' : 'Dias Restantes'}
            </p>
            
            {status === 'Concluído' && (
                <p className="text-sm font-bold mt-2 text-green-600">
                    Concluído em {formatarDataConclusao(data_final)}
                </p>
            )}
        </div>
    );
};